import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { COUNTRIES, CountryFlag } from '../data/countries.tsx'

const COUNTRY_NAMES = Object.fromEntries(COUNTRIES.map((c) => [c.value, c.label]))

/** The display name of a country code, falling back to the code itself. */
export function countryName(code: string) {
  return COUNTRY_NAMES[code] ?? code
}

export const ALL_COUNTRIES = 'all'

interface CountryFilterProps {
  /** The selected country code, or `all` for no filtering. */
  value: string
  onChange: (value: string) => void
  /**
   * The codes on offer — normally the ones actually present in the list being
   * filtered. Leave out to offer every country.
   */
  codes?: string[]
}

/** A country picker for list filters, showing each country with its flag. */
export function CountryFilter({ value, onChange, codes }: CountryFilterProps) {
  const { t } = useTranslation()

  const options = useMemo(() => {
    const list = codes ? [...new Set(codes)].map((c) => ({ value: c, label: countryName(c) })) : COUNTRIES
    return [...list].sort((a, b) => a.label.localeCompare(b.label))
  }, [codes])

  return (
    <Select value={value} onValueChange={(v) => onChange(v ?? ALL_COUNTRIES)}>
      <SelectTrigger className="h-8 w-full text-sm">
        <SelectValue>
          {value === ALL_COUNTRIES ? (
            t('common.all')
          ) : (
            <span className="flex items-center gap-2">
              <CountryFlag code={value} />
              {countryName(value)}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_COUNTRIES}>{t('common.all')}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            <span className="flex items-center gap-2">
              <CountryFlag code={o.value} />
              {o.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
